import { Component, Inject, OnInit } from '@angular/core';
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
} from '@angular/material/dialog';
import { AddUsersDialogData } from '@components/add-users-dialog/1.0';
import { BadgeType } from '@constants/badge/1.0';
import { ButtonSize, ButtonStyle } from '@constants/button/1.0';
import { CustomerUser } from '@generated/tcea/models/customer-user';
import {
  MatTableDataSource,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatFooterCellDef,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
  MatFooterRowDef,
  MatFooterRow,
} from '@angular/material/table';
import { SegmentsService } from '@services/segments/1.0/segments.service';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatFormField, MatPrefix, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { TypeSelectorComponent } from '../../type-selector/1.0/type-selector.component';
import { ButtonComponent } from '../../button/1.0/button.component';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import { NgIf } from '@angular/common';

@Component({
  selector: 'txp-add-users-dialog',
  templateUrl: './add-users-dialog.component.html',
  styleUrls: ['./add-users-dialog.component.scss'],
  standalone: true,
  imports: [
    MatDialogTitle,
    CdkScrollable,
    MatDialogContent,
    MatFormField,
    MatPrefix,
    MatInput,
    TypeSelectorComponent,
    ButtonComponent,
    MatTable,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatCellDef,
    MatCell,
    NgIf,
    MatFooterCellDef,
    MatLabel,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatFooterRowDef,
    MatFooterRow,
    MatDialogActions,
    TranslateModule,
  ],
})
export class AddUsersDialogComponent implements OnInit {
  dataSource: MatTableDataSource<CustomerUser> = new MatTableDataSource<CustomerUser>();
  displayColumns = ['name', 'email', 'id', 'action'];
  buttonStyle = ButtonStyle;
  buttonSize = ButtonSize;
  badgeType = BadgeType; // expose enum to template
  isLoading = false;
  itemsList: CustomerUser[] = [];
  addedItems: { [id: string]: CustomerUser };
  searchText: string = '';
  searchDone = false;
  searchTypes;
  selectedSearchType;

  constructor(
    private dialogRef: MatDialogRef<AddUsersDialogComponent>,
    private segmentsService: SegmentsService,
    private translateService: TranslateService,
    @Inject(MAT_DIALOG_DATA) public data: AddUsersDialogData,
  ) {
    this.addedItems = data.selectedList
      .slice()
      .reduce((obj: { [id: string]: CustomerUser }, item) => {
        obj[item.userId] = item;
        return obj;
      }, {});

    this.searchTypes = [
      {
        data: 'email',
        label: this.translateService.instant('coupons.segments.grid.users.modal.emailType'),
      },
      {
        data: 'userId',
        label: this.translateService.instant('coupons.segments.grid.users.modal.idType'),
      },
      {
        data: 'phoneNumber',
        label: this.translateService.instant('coupons.segments.grid.users.modal.phoneType'),
      },
    ];
    this.selectedSearchType = this.searchTypes[0].data;
  }

  ngOnInit() {
    this.dataSource.data = [...Object.values(this.addedItems)];
  }

  createSearchParams() {
    const searchType = this.selectedSearchType;
    const params = new URLSearchParams();

    if (!this.searchText) {
      return '';
    }

    this.searchText
      // removing spaces
      .replace(/ /g, '')
      .split(',')
      .forEach((filter) => {
        if (searchType !== 'userId') {
          filter = encodeURIComponent(filter);
        }

        params.append(searchType, filter);
      });

    return params.toString() ? `?${params.toString()}` : undefined;
  }

  getUsers() {
    if (!this.searchText) {
      return;
    }

    const brandId = window.sessionStorage.getItem('brand_id');
    const params = this.createSearchParams();

    this.isLoading = true;
    this.segmentsService.getCustomerUsers(brandId, params).subscribe((users) => {
      this.isLoading = false;

      this.itemsList = users.filter((user) => !this.addedItems[user.userId]);
      this.dataSource.data = [...Object.values(this.addedItems), ...this.itemsList];
      this.searchDone = true;
    });
  }

  handleDone() {
    this.dialogRef.close(Object.values(this.addedItems));
  }

  handleCancel() {
    this.dialogRef.close();
  }

  addUser(user: CustomerUser) {
    this.addedItems[user.userId] = user;
  }

  removeUser(user: CustomerUser) {
    delete this.addedItems[user.userId];
  }

  protected readonly ButtonSize = ButtonSize;
}
